/**
 * Phone Number Utilities
 * 
 * Handles phone number formatting for display and storage
 * Storage format: +1XXXXXXXXXX (E.164 format)
 * Display format: XXX-XXX-XXXX (US format without country code)
 */

/**
 * Extract only digits from a phone number string
 */
export function getDigits(phone: string | undefined | null): string {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

/**
 * Format phone number for storage (E.164 format: +1XXXXXXXXXX)
 * Accepts any input format and extracts 10 digits
 * 
 * @throws Error if phone doesn't contain exactly 10 digits
 */
export function formatPhoneForStorage(phone: string): string {
  const digits = getDigits(phone);
  if (digits.length !== 10) {
    throw new Error('Phone number must contain exactly 10 digits');
  }
  return '+1' + digits;
}

/**
 * Format phone number for display (XXX-XXX-XXXX)
 * Removes +1 country code and formats as XXX-XXX-XXXX
 * 
 * Handles:
 * - +16785708358 → 678-570-8358
 * - 16785708358 → 678-570-8358
 * - 6785708358 → 678-570-8358
 * - (678) 570-8358 → 678-570-8358
 */
export function formatPhoneForDisplay(phone: string | undefined | null): string {
  if (!phone) return '';
  
  const digits = getDigits(phone);
  
  // Handle 11 digits starting with 1 (remove country code)
  let phoneDigits: string;
  if (digits.length === 11 && digits.startsWith('1')) {
    phoneDigits = digits.substring(1);
  } else if (digits.length === 10) {
    phoneDigits = digits;
  } else {
    // Return original if not standard format
    return phone;
  }
  
  // Format as XXX-XXX-XXXX
  return `${phoneDigits.substring(0, 3)}-${phoneDigits.substring(3, 6)}-${phoneDigits.substring(6)}`;
}

/**
 * Format phone number for contact display: (XXX) XXX-XXXX
 *
 * This is the parenthesized style used by the Dashboard rider card's Contact
 * Information block and by the Rider Detail "Associated Users" section. It is
 * deliberately separate from `formatPhoneForDisplay`, which produces the dashed
 * `XXX-XXX-XXXX` form used by Profile, admin/Users, Riders and the phone prompt
 * modal. Both formats are in production; neither replaces the other.
 *
 * PRESENTATION ONLY. This function does not decide WHETHER a number should be
 * shown -- that is `isDisplayablePhone`. The split exists because the Dashboard
 * must keep its exact current behaviour (it renders whatever it gets, including
 * malformed values) while Rider Detail applies a stricter N/A policy.
 *
 * Handles:
 * - +17705956403   -> (770) 595-6403
 * - 17705956403    -> (770) 595-6403
 * - 7705956403     -> (770) 595-6403
 * - (770) 595-6403 -> (770) 595-6403   (idempotent)
 * - 12345          -> 12345            (pass-through, unformattable)
 * - '' / null      -> ''
 *
 * ---------------------------------------------------------------------------
 * RELATIONSHIP TO THE EXPRESSION THIS REPLACED
 *
 * Dashboard.tsx previously inlined, twice:
 *   phone.replace(/^\+1/, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
 *
 * For every value Cognito can actually store -- `phone_number` is E.164, i.e.
 * always `+1XXXXXXXXXX` -- this function is byte-identical to that expression.
 * It also matches for bare 10-digit input and for already-formatted input.
 *
 * It DELIBERATELY DIFFERS for inputs that are neither a plain 10-digit number
 * nor E.164, because the old expression mis-formatted them:
 *
 *   '17705956403'   old: (177) 059-56403   new: (770) 595-6403
 *   '770595640312'  old: (770) 595-640312  new: 770595640312
 *
 * The old output there is not a phone number in any format; it grabbed the first
 * ten digits it found and left the rest dangling. Reproducing that faithfully
 * would also have put this function in direct conflict with
 * `isDisplayablePhone`, which accepts '17705956403' as a valid number -- so a
 * caller could be told a value was displayable and then be handed garbage to
 * display. A property test caught exactly that contradiction.
 *
 * These inputs cannot arise from Cognito data, so no rendered output changes in
 * production. See `phone.test.ts` -> "F-13" for the parity table and the
 * explicit divergence cases.
 * ---------------------------------------------------------------------------
 *
 * NOTE: never use the result to build a `tel:` or `sms:` href -- use the raw
 * stored E.164 value for those, so dialers behave predictably.
 */
export function formatPhoneParenthesized(phone: string | undefined | null): string {
  if (!phone) return '';

  const digits = getPhoneDigitsOnly(phone);
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Unformattable: mirror the old expression's pass-through, which stripped a
  // leading +1 and returned the value otherwise untouched.
  return phone.replace(/^\+1/, '');
}

/**
 * Placeholder phone number used during account migration
 */
const PLACEHOLDER_PHONE = '1111111111';

/**
 * Whether a phone number should be rendered as contact information at all.
 *
 * POLICY, not presentation. Returns true only when the value carries exactly
 * ten significant US digits AND is not the migration placeholder. Anything else
 * -- missing, blank, malformed, or the placeholder -- is not displayable, and
 * the caller should render "N/A" with no link rather than a link to a number
 * that does not work.
 *
 * As of 2026-08-10 this affects 14 of 131 users: 8 have no phone number at all
 * and 6 still carry the `1111111111` placeholder.
 *
 * Total function: no input can cause it to throw.
 */
export function isDisplayablePhone(phone: string | undefined | null): boolean {
  const digits = getPhoneDigitsOnly(phone);
  if (digits.length !== 10) return false;
  return digits !== PLACEHOLDER_PHONE;
}

/**
 * Determine whether the user should be prompted to provide a real phone number.
 * Returns true when the phone is missing, empty, or equals the placeholder value.
 */
export function needsPhonePrompt(user: { phone?: string } | null | undefined): boolean {
  if (!user) return true;
  if (!user.phone) return true;
  if (!user.phone.trim()) return true;
  return getPhoneDigitsOnly(user.phone) === PLACEHOLDER_PHONE;
}

/**
 * Validate a phone number for the phone prompt modal.
 * Returns { isValid: true } for valid 10-digit numbers that are not the placeholder,
 * or { isValid: false, error: string } with an appropriate message.
 */
export function validatePhoneForPrompt(phone: string): { isValid: boolean; error?: string } {
  const digits = getDigits(phone);
  if (digits.length !== 10) {
    return { isValid: false, error: 'Phone number must contain exactly 10 digits' };
  }
  if (digits === PLACEHOLDER_PHONE) {
    return { isValid: false, error: 'This placeholder number is not accepted. Please enter your real phone number.' };
  }
  return { isValid: true };
}

/**
 * Get phone digits only (10 digits, no country code)
 * Used for validation and comparison
 */
export function getPhoneDigitsOnly(phone: string | undefined | null): string {
  if (!phone) return '';
  
  const digits = getDigits(phone);
  
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.substring(1);
  } else if (digits.length === 10) {
    return digits;
  }
  
  return digits;
}
